import TierBanner from '@/components/banners/TierBanner';
import { CustomButton } from '@/components/buttons/Buttons';
import { Chip } from 'primereact/chip';
import { Divider } from 'primereact/divider';
import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';
import { classNames } from 'primereact/utils';
import { useRef } from 'react';
import { MdMoreVert } from 'react-icons/md';
import style from '../../views/employee.module.css';
import { showTypedModal } from '@/common/common_utils';
import { X4bEmployeeDetailsDto as X4bEmployeeDetailsDto } from '@/types';
import moment from 'moment';
import { UseQueryResult } from '@tanstack/react-query';
import EditPeopleModal from '../../views/EditPeopleScreen';
import { useAuthenticationStore } from '@/store';
import { X4bUserRoleEnum } from '@/constants/roles';

type Employee = {
	name: string;
	phone: string;
	email: string;
	employeeID: string;
	gender: string;
	loginMethod: string;
	city: string;
	inviteCode: string;
	role: string;
	group: string;
	program: string;
	createdAt: string;
	lastActive: string;
};

type Props = {
	employeeQ: UseQueryResult<X4bEmployeeDetailsDto>;
};


const EmployeeDetails = (props: Props) => {
	const { employeeQ } = props;
	const employee = employeeQ.data!;
	//const [editType, setEditType] = useState<EditType>('single');
	const menu = useRef<Menu>(null);
	const isManagerOrAdmin = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.Manager, X4bUserRoleEnum.Admin]);
	//const [openEditModal, setOpenEditModal] = useState(false);

	const items: MenuItem[] = [
		{
			label: 'Edit Details',
			command: async () => {
				await showTypedModal(EditPeopleModal, { type: 'single', employeeId: employee.Id! });
				await employeeQ.refetch();
			},
		},
	];

	return (
		<div>
			<TierBanner
				image={{
					src: '/images/svg/Loyalty.svg',
					width: 56,
					height: 56,
					alt: 'tier',
				}}
				className=" bg-warning99 mb-4"
				content={
					<div>
						<h1 className="text-label-large font-medium">
							Gold Subscription
						</h1>
						<p className="text-label-x-small">
							Our premium pax. Please be warm and cordial to them
						</p>
					</div>
				}
			/>

			<div className="flex items-center gap-4">
				<img
					alt="avatar"
					src={'/images/svg/avatar.svg'}
					width={120}
					height={120}
					className="rounded-full"
				/>
				<div className="flex-1 space-y-6">
					<div className="flex justify-between items-center">
						<div>
							<p className="font-medium text-label-large">
								{employee.FirstName} {employee.LastName}
							</p>
							<p className="text-neutral40 text-label-large text-base">
								{employee.Phone}
							</p>
						</div>
						<div className="p-2">
							{isManagerOrAdmin &&
								<CustomButton
									id="edit-employee"
									onClick={(event) => menu.current?.toggle(event)}
									aria-controls="edit-employee"
									aria-haspopup="true"
									rounded
									className="p-3 hover:bg-surfaceContainerHighest"
								>
									<MdMoreVert
										size={24}
										className="text-primary"
									/>
								</CustomButton>
							}
							<Menu
								model={items}
								popupAlignment="right"
								popup
								ref={menu}
								id="edit-employee"
								pt={{
									root: {
										className: classNames(
											style.employeeMenu,
										),
									},
									content: {
										className: classNames(
											style.employeeMenu,
										),
									},
								}}
							/>
						</div>
					</div>

					<div className="grid md:grid-cols-2">
						<div>
							<h1 className="text-label-x-small">Signup Date</h1>
							<p className="text-neutral40 text-[10px]">
								{employee.SignUpDate ? moment(employee.SignUpDate).format('MMMM D, YYYY') : '-'}
							</p>
						</div>

						<div>
							<h1 className="text-label-x-small">Last Active</h1>
							<p className="text-neutral40 text-[10px]">
								{employee.LastActive ? moment(employee.LastActive).fromNow() : '-'}
							</p>
						</div>
					</div>
				</div>
			</div>
			<Divider />
			<div className="flex flex-col">
				<div className="flex justify-between py-2 items-center">
					<span className="text-label-small">Employee ID</span>
					<span className="text-label-small text-neutral40">
						{employee.ReferenceCode}
					</span>
				</div>
				<div className="flex justify-between py-2 items-center">
					<span className="text-label-small">Gender</span>
					<span className="text-label-small text-neutral40">
						{employee.Gender}
					</span>
				</div>
				<div className="flex justify-between py-2 items-center">
					<span className="text-label-small">Email</span>
					<a
						href={`mailto:${employee.Email}`}
						className="text-tertiary text-label-small"
					>
						{employee.Email}
					</a>
				</div>
				<div className="flex justify-between py-2 items-center">
					<span className="text-label-small">
						Preferred Login Method
					</span>
					<span className="text-label-small text-neutral40">
						{employee.PreferredLodinMethod}
					</span>
				</div>
				<div className="flex justify-between py-2 items-center">
					<span className="text-label-small">Last Known City</span>
					<span className="text-label-small text-neutral40">
						{employee.LastKnownCity}
					</span>
				</div>
				<Divider />
				<div className="flex justify-between py-2 items-center">
					<span className="text-label-small">Invite Code</span>
					<span className="text-label-small text-neutral40">
						{employee.InviteCode}
					</span>
				</div>
				<div className="flex justify-between py-2 items-center">
					<span className="text-label-small">Role</span>
					<span className="text-label-small text-neutral40">
						{employee.RoleNames}
					</span>
				</div>
				<div className="flex justify-between py-2 items-center">
					<span className="text-label-small">Group</span>
					<span className="text-label-small text-neutral40">
						{employee.Group_Name}
					</span>
				</div>
				<div className="flex justify-between items-center">
					<span className="text-label-small">Program</span>
					<Chip
						label={'Travel Program'}
						image="/images/svg/Service Icons.svg"
						pt={{
							root: {
								className: style.employeeProgramChip,
							},
							image: {
								className: style.employeeProgramChipImage,
							},
						}}
					/>
				</div>
				<Divider />
				<div className="flex justify-between py-2 items-center">
					<span className="text-label-small">Was Referred?</span>
					<span className="text-label-small text-neutral40">
						-
						{/* {employee.WasReferred ? 'Yes' : 'No'} */}
					</span>
				</div>
				<div className="flex justify-between py-2 items-center">
					<span className="text-label-small">Referrer</span>
					<span className="text-label-small text-neutral40">
						-
						{/* {employee.Referrer} */}
					</span>
				</div>
				<Divider />
			</div>

		</div>
	);
};

export default EmployeeDetails;
